import { useEffect, useState, useRef } from 'react';
import { usePxToVw } from '../hooks/usePxToVw';

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const CloudIcon = ({ size = 32, color = '#64748b' }) => (
  <svg width='100%' height='100%' viewBox="0 0 64 64" fill="none">
    <path d="M48 40H18a10 10 0 1 1 2.08-19.78A14 14 0 1 1 48 40z" fill={color} />
  </svg>
);

const RainIcon = ({ size = 32, color = '#64748b' }) => (
  <svg width='100%' height='100%' viewBox="0 0 64 64" fill="none">
    <path d="M46 34H18a10 10 0 1 1 2.08-19.78A14 14 0 1 1 46 34z" fill={color} />
    {[24, 32, 40].map((x, i) => (
      <line key={i} x1={x} y1="40" x2={x - 2} y2="52" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.8" />
    ))}
  </svg>
);

const SunIcon = ({ size = 32 }) => (
  <svg width='100%' height='100%' viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="12" fill="#FFD700" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => {
      const rad = (deg * Math.PI) / 180;
      return (
        <line key={i}
          x1={32 + 17 * Math.cos(rad)} y1={32 + 17 * Math.sin(rad)}
          x2={32 + 24 * Math.cos(rad)} y2={32 + 24 * Math.sin(rad)}
          stroke="#FFD700" strokeWidth="3" strokeLinecap="round" />
      );
    })}
  </svg>
);

const PartlyCloudyIcon = ({ size = 32 }) => (
  <svg width='100%' height='100%' viewBox="0 0 64 64" fill="none">
    <circle cx="22" cy="24" r="10" fill="#FFD700" />
    {[0, 60, 120, 180, 240, 300].map((deg, i) => {
      const rad = (deg * Math.PI) / 180;
      return (
        <line key={i}
          x1={22 + 13 * Math.cos(rad)} y1={24 + 13 * Math.sin(rad)}
          x2={22 + 17 * Math.cos(rad)} y2={24 + 17 * Math.sin(rad)}
          stroke="#FFD700" strokeWidth="2.5" strokeLinecap="round" />
      );
    })}
    <path d="M50 44H28a9 9 0 1 1 1.87-17.8A12 12 0 1 1 50 44z" fill="rgba(100,116,139,0.9)" />
  </svg>
);

const ThunderIcon = ({ size = 32, color = '#64748b' }) => (
  <svg width='100%' height='100%' viewBox="0 0 64 64" fill="none">
    <path d="M46 34H20a9 9 0 1 1 1.87-17.8A13 13 0 1 1 46 34z" fill={color} />
    <polyline points="34,36 27,50 33,50 29,60 42,44 35,44" fill="#FFD700" stroke="#FFD700" strokeWidth="1" strokeLinejoin="round" />
  </svg>
);

const SnowIcon = ({ size = 32, color = '#64748b' }) => (
  <svg width='100%' height='100%' viewBox="0 0 64 64" fill="none">
    <path d="M46 34H20a9 9 0 1 1 1.87-17.8A13 13 0 1 1 46 34z" fill={color} />
    {[22, 32, 42].map((x, i) => (
      <g key={i}>
        <line x1={x} y1="40" x2={x} y2="54" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <line x1={x - 4} y1="44" x2={x + 4} y2="44" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <line x1={x - 4} y1="50" x2={x + 4} y2="50" stroke={color} strokeWidth="2" strokeLinecap="round" />
      </g>
    ))}
  </svg>
);

const MistIcon = ({ size = 32, color = '#64748b' }) => (
  <svg width='100%' height='100%' viewBox="0 0 64 64" fill="none">
    {[16, 24, 32, 40, 48].map((y, i) => (
      <line key={i} x1={i % 2 === 0 ? 8 : 14} y1={y} x2={i % 2 === 0 ? 56 : 50} y2={y}
        stroke={color} strokeWidth="3" strokeLinecap="round" opacity={0.4 + i * 0.12} />
    ))}
  </svg>
);

function getIconComponent(iconCode) {
  if (!iconCode) return CloudIcon;
  const id = iconCode.replace(/[dn]$/, '');
  if (id === '01') return SunIcon;
  if (['02', '03'].includes(id)) return PartlyCloudyIcon;
  if (id === '04') return CloudIcon;
  if (['09', '10'].includes(id)) return RainIcon;
  if (id === '11') return ThunderIcon;
  if (id === '13') return SnowIcon;
  if (id === '50') return MistIcon;
  return CloudIcon;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function parseForecast(list, days = 3) {
  const result = {};
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const todayIdx = new Date().getDay();
  for (const item of list) {
    const date = new Date(item.dt * 1000);
    const dayIdx = date.getDay();
    if (dayIdx === todayIdx) continue;
    const key = date.toDateString();
    if (!result[key]) {
      result[key] = { day: dayNames[dayIdx], icon: item.weather[0].icon, high: item.main.temp_max, low: item.main.temp_min };
    } else {
      if (item.main.temp_max > result[key].high) result[key].high = item.main.temp_max;
      if (item.main.temp_min < result[key].low) result[key].low = item.main.temp_min;
    }
    if (Object.keys(result).length === days) break;
  }
  return Object.values(result);
}

function getLocalDateTime(timezoneOffset) {
  const utcMs = Date.now() + new Date().getTimezoneOffset() * 60000;
  const d = new Date(utcMs + timezoneOffset * 1000);
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return {
    time: `${String(h).padStart(2, '0')}:${m}`,
    ampm,
    day: days[d.getDay()].toUpperCase(),
    date: `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`,
  };
}

const toF = (c) => Math.round((c * 9) / 5 + 32);

function formatTemp(c, unit, celsiusFirst) {
  const cStr = `${Math.round(c)}°C`;
  const fStr = `${toF(c)}°F`;
  if (unit === 'celsius') return cStr;
  if (unit === 'fahrenheit') return fStr;
  return celsiusFirst ? `${cStr} / ${fStr}` : `${fStr} / ${cStr}`;
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function WeatherWidget({
  apiKey = '',
  latitude = null,
  longitude = null,
  forecastDays = 3,
  temperatureUnit = 'both',
  celsiusFirst = true,
  // showLocation and showClock are now INDEPENDENT toggles
  showLocation = false,   // shows city/country name
  showClock = true,       // shows time (HH:MM AM/PM)
  showCurrentWeather = true,
  showForecast = true,
  widgetOrder = ['clock', 'current', 'forecast'],
  backgroundColor = '#ffffff',
  backgroundOpacity = 80,
  textColor = '#1a202c',
  subTextColor = '#718096',
  padding = 16,
  borderRadius = 12,
  width = 0,
  fontFamily = 'inherit',
  fontSize = 14,
  iconSize = 40,
  forecastIconSize = 28,
  // Optional: use vw-based sizing for kiosk screens
  useVwSizing = false,
  baseWidth = 1920,
  // Box shadow
  shadowH = 0,
  shadowV = 4,
  shadowBlur = 12,
  shadowSpread = 0,
  shadowColor = '#000000',
  shadowOpacity = 15,
}) {
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [clock, setClock] = useState(null);
  const [timezoneOffset, setTimezoneOffset] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const clockRef = useRef(null);

  const effectiveApiKey = apiKey || 'ce4d6c7071e49974ccc04a76cc5a36a4';

  const { pxToVw } = usePxToVw();

  // sz: use hook's pxToVw (which respects 4K base width) when useVwSizing is on, else plain px
  const sz = (px) => useVwSizing ? pxToVw(px) : `${px}px`;

  const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

  const getCacheKey = (lat, lon) => `weatherCache_${lat}_${lon}`;

  const loadFromCache = (lat, lon) => {
    try {
      const raw = localStorage.getItem(getCacheKey(lat, lon));
      if (!raw) return null;
      const { timestamp, cData, fData } = JSON.parse(raw);
      if (Date.now() - timestamp > CACHE_TTL_MS) return null; // expired
      return { cData, fData };
    } catch {
      return null;
    }
  };

  const saveToCache = (lat, lon, cData, fData) => {
    try {
      localStorage.setItem(getCacheKey(lat, lon), JSON.stringify({ timestamp: Date.now(), cData, fData }));
    } catch { /* storage full or unavailable */ }
  };

  useEffect(() => {
    const applyWeatherData = (cData, fData) => {
      setWeather(cData);
      setTimezoneOffset(cData.timezone);
      setForecast(parseForecast(fData.list, forecastDays));
      setLoading(false);
    };

    const fetchWeather = async (lat, lon) => {
      // Use cached data if still fresh
      const cached = loadFromCache(lat, lon);
      if (cached) {
        applyWeatherData(cached.cData, cached.fData);
        return;
      }

      try {
        const [cRes, fRes] = await Promise.all([
          fetch(`https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&units=metric&appid=${effectiveApiKey}`),
          fetch(`https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&units=metric&appid=${effectiveApiKey}`),
        ]);
        if (!cRes.ok) throw new Error('API error');
        const cData = await cRes.json();
        const fData = await fRes.json();
        saveToCache(lat, lon, cData, fData);
        applyWeatherData(cData, fData);
      } catch (e) {
        setError('Could not fetch weather data.');
        setLoading(false);
      }
    };

    if (latitude !== null && longitude !== null) {
      fetchWeather(latitude, longitude);
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude: lat, longitude: lon } }) => fetchWeather(lat, lon),
        () => { setError('Location access denied.'); setLoading(false); }
      );
    } else {
      setError('Geolocation not supported.');
      setLoading(false);
    }
  }, [apiKey, latitude, longitude, forecastDays]);

  useEffect(() => {
    if (timezoneOffset === null) return;
    const tick = () => setClock(getLocalDateTime(timezoneOffset));
    tick();
    clockRef.current = setInterval(tick, 1000);
    return () => clearInterval(clockRef.current);
  }, [timezoneOffset]);

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '255,255,255';
  };

  const bgRgba = `rgba(${hexToRgb(backgroundColor)}, ${backgroundOpacity / 100})`;

  const shadowRgba = `rgba(${hexToRgb(shadowColor)}, ${shadowOpacity / 100})`;
  const boxShadow = `${shadowH}px ${shadowV}px ${shadowBlur}px ${shadowSpread}px ${shadowRgba}`;

  const containerStyle = {
    display: 'inline-flex',
    alignItems: 'stretch',
    background: bgRgba,
    borderRadius: sz(borderRadius),
    padding: sz(padding),
    fontFamily: fontFamily !== 'inherit' ? fontFamily : undefined,
    fontSize: sz(fontSize),
    color: textColor,
    width: width > 0 ? sz(width) : 'auto',
    boxSizing: 'border-box',
    gap: '0',
    boxShadow,
  };

  const dividerStyle = {
    width: '1px',
    background: `rgba(${hexToRgb(textColor)}, 0.15)`,
    margin: `0 ${sz(padding)}`,
    flexShrink: 0,
    alignSelf: 'stretch',
  };

  const iconColor = textColor;

  if (loading) {
    return (
      <div style={{ ...containerStyle }}>
        <span style={{ color: subTextColor }}>Loading weather...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...containerStyle }}>
        <span style={{ color: subTextColor }}>{error}</span>
      </div>
    );
  }

  const iconCode = weather?.weather?.[0]?.icon;
  const CurrentIcon = getIconComponent(iconCode);
  const tempC = weather?.main?.temp ?? 0;
  const condition = weather?.weather?.[0]?.description ?? '';
  const city = weather?.name ?? '';
  const country = weather?.sys?.country ?? '';

  // ── Build sections ──────────────────────────────────────────────────────────
  // The 'clock' section in widgetOrder now renders BOTH location name AND clock time
  // independently based on their own toggles. If neither is on, the section is skipped.
  const sections = [];

  widgetOrder.forEach((section) => {
    if (section === 'clock') {
      // Only render this panel if at least one of location or clock is enabled
      const hasLocation = showLocation && (city || country);
      const hasClock = showClock && clock;
      if (!hasLocation && !hasClock) return;

      sections.push(
        <div key="clock" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: sz(80) }}>
          {/* Location name — independent toggle */}
          {hasLocation && (
            <div style={{
              fontWeight: 700,
              fontSize: sz(fontSize * 1.1),
              color: textColor,
              lineHeight: 1.2,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: hasClock ? sz(2) : 0,
            }}>
              {city}{country ? `, ${country}` : ''}
            </div>
          )}
          {/* Clock time — independent toggle */}
          {hasClock && (
            <div style={{ color: subTextColor, fontSize: sz(fontSize * 0.9) }}>
              {clock.time} {clock.ampm}
            </div>
          )}
        </div>
      );
    }

    if (section === 'current' && showCurrentWeather) {
      sections.push(
        <div key="current" style={{ display: 'flex', alignItems: 'center', gap: sz(padding * 0.6) }}>
          <div style={{ width: sz(iconSize), height: sz(iconSize), flexShrink: 0 }}>
            <CurrentIcon size={iconSize} color={iconColor} />
          </div>
          <div>
            <div style={{
              fontWeight: 800,
              fontSize: sz(fontSize * 1.6),
              color: textColor,
              lineHeight: 1,
              letterSpacing: '-0.5px',
            }}>
              {formatTemp(tempC, temperatureUnit, celsiusFirst)}
            </div>
            <div style={{ color: subTextColor, fontSize: sz(fontSize * 0.9), marginTop: sz(2), textTransform: 'capitalize' }}>
              {condition}
            </div>
          </div>
        </div>
      );
    }

    if (section === 'forecast' && showForecast && forecast.length > 0) {
      sections.push(
        <div key="forecast" style={{ display: 'flex', alignItems: 'center', gap: sz(padding) }}>
          {forecast.map((day) => {
            const FIcon = getIconComponent(day.icon);
            return (
              <div key={day.day} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: sz(2), minWidth: sz(50),
              }}>
                <div style={{ fontWeight: 700, fontSize: sz(fontSize * 0.85), color: subTextColor, letterSpacing: '1px' }}>
                  {day.day}
                </div>
                <div style={{ width: sz(forecastIconSize), height: sz(forecastIconSize), flexShrink: 0 }}>
                  <FIcon size={forecastIconSize} color={iconColor} />
                </div>
                <div style={{ color: '#e05c5c', fontWeight: 700, fontSize: sz(fontSize * 0.85) }}>
                  {formatTemp(day.high, temperatureUnit, celsiusFirst)}
                </div>
                <div style={{ color: '#5599cc', fontWeight: 600, fontSize: sz(fontSize * 0.8) }}>
                  {formatTemp(day.low, temperatureUnit, celsiusFirst)}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  });

  // Interleave dividers
  const renderedSections = [];
  sections.forEach((section, idx) => {
    renderedSections.push(section);
    if (idx < sections.length - 1) {
      renderedSections.push(<div key={`div-${idx}`} style={dividerStyle} />);
    }
  });

  return (
    <div style={containerStyle}>
      {renderedSections}
    </div>
  );
}
